import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import * as Sentry from '@sentry/react';

import AppBar from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import Button from 'components/Buttons/Button';
import ConfirmBottomSheet from 'components/ConfirmBottomSheet';
import Loader from 'components/Loader';
import PopoverCardItem from 'components/Payment/PopoverCardItem';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { PaymentCardData, PaymentGroup } from 'models/paymentCard';
import CompanyProfileSuccess from 'screens/components/CompanyProfileSuccess';
import PaymentCardNoCard from 'screens/components/Payment/PaymentCardNoCard';
import SimpleButton from 'screens/components/Payment/PaymentCardNoCard/components/SimpleButton';
import PaymentHelp from 'screens/components/Payment/PaymentHelp';
import ButtonHelp from 'screens/components/Payment/PaymentHelp/components/ButtonHelp';
import PaymentLinkGroup from 'screens/components/Payment/PaymentLinkGroup';
import { changeDefaultCard, removeCard, removeGroupsCard } from 'services/paymentMethodService';
import { htmlT } from 'utils/generic';
import { cardTypeNormalize } from 'utils/payment';

import AggregateGroups from './components/AggregateGroups';
import StyledAlertBottomSheet from './components/StyledAlertBottomSheet';
import { SectionContainer, Content, Title, Spacer, TextModal, ButtonContainer } from './styles';

interface Props {
  cardList: PaymentCardData[];
  isLoading: boolean;
  onUpdate: () => void;
}

interface AlertData {
  show: boolean;
  title?: string;
  text?: string;
  button?: string;
}
interface ConfirmData {
  show: boolean;
  title?: string;
  text?: string;
  onConfirm?: () => void;
}

const PaymentCardSelection = ({ cardList, isLoading, onUpdate }: Props) => {
  const { t } = useStoreTranslation();
  const history = useHistory<{ completingCompanyProfile?: boolean }>();
  const toast = useToast();
  const [alertData, setAlertData] = useState<AlertData>({ show: false });
  const [confirmData, setConfirmData] = useState<ConfirmData>({ show: false });
  const [cardResult, setCardResult] = useState<PaymentCardData[]>([]);
  const [cardDefault, setCardDefault] = useState<PaymentCardData | undefined>(undefined);
  const [selectedCard, setSelectedCard] = useState<PaymentCardData | undefined>(undefined);
  const [showLinkCard, setShowLinkCard] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const isCompletingCompanyProfile = !!history.location.state?.completingCompanyProfile;

  const handleNavigateHistory = () => {
    history.push('/dashboard');
  };

  const renderLinkCard = () => (
    <ApplicationOverlayContent sidebar show={showLinkCard} onClose={() => setShowLinkCard(false)}>
      <PaymentLinkGroup
        selectedCard={selectedCard?.card}
        defaultCard={cardDefault?.card}
        onClose={() => setShowLinkCard(false)}
        onUpdate={() => {
          setShowLinkCard(false);
          onUpdate();
        }}
      />
    </ApplicationOverlayContent>
  );

  const renderHelp = () => (
    <ApplicationOverlayContent sidebar show={showHelp} onClose={() => setShowHelp(false)}>
      <PaymentHelp onClose={() => setShowHelp(false)} title={t('payment.title')} />
    </ApplicationOverlayContent>
  );

  useEffect(() => {
    const cardListFiltered = cardList.filter((data) => !data.card.default_cc);
    const cardDefaultList = cardList.filter((data) => data.card.default_cc);
    setCardDefault(cardDefaultList[0]);

    setCardResult(cardListFiltered);
  }, [cardList]);

  const handleRemove = (card: PaymentCardData, cardDefault?: PaymentCardData, linkedGroups?: PaymentGroup[]) => {
    const isDefault = card.card.id === cardDefault?.card.id;

    if (isDefault) {
      setAlertData({
        show: true,
        title: t('payment.paymentCardGroup.alertNoDefault.title'),
        text: t('payment.paymentCardGroup.alertNoDefault.text'),
        button: t('payment.paymentCardGroup.alertNoDefault.button'),
      });
    } else {
      if (linkedGroups && linkedGroups.length === 0) {
        handleRemoveCard(card);
      } else {
        setConfirmData({
          show: true,
          title: t('payment.paymentCardGroup.alertRemoveCard.title'),
          text: `${htmlT(t, 'payment.paymentCardGroup.alertRemoveCard.text', {
            card: `${cardDefault ? cardTypeNormalize(cardDefault.card.card_type).toUpperCase() : ''} *${
              cardDefault ? cardDefault.card.last_four_digits : ''
            }`,
          })}`,
          onConfirm: () => {
            handleRemoveCard(card);
          },
        });
      }
    }
  };

  const handleSetAsDefault = (data: PaymentCardData) => {
    setConfirmData({
      show: true,
      title: t('payment.paymentCardGroup.alertChangeDefault.title'),
      text: t('payment.paymentCardGroup.alertChangeDefault.text'),
      onConfirm: () => {
        handleChangeCard(data.card.id);
      },
    });
  };

  const handleLinkToGroup = (card: PaymentCardData) => {
    setSelectedCard(card);
    setShowLinkCard(true);
  };

  const handleRemoveCard = useCallback(
    async (cardData: PaymentCardData) => {
      setIsSaving(true);
      try {
        if (typeof cardData.linkedGroups !== 'undefined' && cardData.linkedGroups.length > 0) {
          await removeGroupsCard(
            cardData.card.id,
            cardData.linkedGroups.map((l) => l.id),
          );
          await removeCard(cardData.card.id);
          onUpdate();
        } else {
          await removeCard(cardData.card.id);
          onUpdate();
        }
      } catch (error) {
        Sentry.addBreadcrumb({
          category: 'payment',
          message: 'Could not remove card',
          data: { error, message: error.message },
        });
        Sentry.captureMessage('[PaymentCardSelection] Could not remove card');

        toast.add({
          type: 'danger',
          message: t('payment.errors.remove.generic'),
        });
      } finally {
        setIsSaving(false);
      }
    },
    [onUpdate, t, toast],
  );

  const handleChangeCard = useCallback(
    async (cardId: number) => {
      setIsSaving(true);
      try {
        await changeDefaultCard(cardId);
        onUpdate();
      } catch (error) {
        toast.add({
          type: 'danger',
          message: error.message,
        });
      } finally {
        setIsSaving(false);
      }
    },
    [toast, onUpdate],
  );

  return (
    <>
      <Mobile>
        <AppBar
          goBack={handleNavigateHistory}
          rightAction={() => history.push('/dashboard')}
          rightLabel={isCompletingCompanyProfile && !isLoading && cardList.length === 0 ? t('payment.skip') : undefined}
        >
          <h5>{t('payment.title')}</h5>
        </AppBar>
      </Mobile>

      {isLoading || isSaving ? (
        <Loader fullScreen />
      ) : (
        <>
          <Desktop>
            <ScreenHeader title={t('payment.title')} />
          </Desktop>
          {cardList.length === 0 ? (
            <>
              <PaymentCardNoCard
                onClick={() => history.push('/paymentMethods/add', history.location.state)}
                isUser={true}
              />
            </>
          ) : (
            <SectionContainer>
              <Title>
                {t('payment.subtitle')}
                <ButtonHelp onClick={() => setShowHelp(true)} />
              </Title>
              {renderLinkCard()}
              {renderHelp()}
              {typeof cardDefault !== 'undefined' ? (
                <Content>
                  <h3>{t('payment.cardList.default.title')}</h3>
                  <PopoverCardItem
                    card={cardDefault.card}
                    isDefault={true}
                    onRemove={() => handleRemove(cardDefault, cardDefault)}
                  />
                </Content>
              ) : null}
              {cardResult.length === 0 ? null : (
                <Content>
                  <h3>{t('payment.cardList.otherCard')}</h3>
                  {cardResult.map((card, idx) => {
                    const linkedGroups = typeof card.linkedGroups !== 'undefined' ? card.linkedGroups : [];
                    return (
                      <AggregateGroups key={idx} groups={linkedGroups}>
                        <PopoverCardItem
                          card={card.card}
                          onRemove={() => handleRemove(card, cardDefault, linkedGroups)}
                          onSetAsDefault={() => handleSetAsDefault(card)}
                          onLinkToGroup={() => handleLinkToGroup(card)}
                        />
                      </AggregateGroups>
                    );
                  })}
                </Content>
              )}
              <Spacer />
              {isCompletingCompanyProfile ? (
                <ButtonContainer>
                  <Button onClick={() => setShowSuccess(true)}>{t('payment.cardList.btnSave')}</Button>
                </ButtonContainer>
              ) : (
                <SimpleButton
                  onClick={() => history.push('/paymentMethods/add', history.location.state)}
                  label={t('payment.cardList.btnAddCard')}
                />
              )}
              <StyledAlertBottomSheet
                show={alertData.show}
                title={alertData.title ? alertData.title : ''}
                close={alertData.button ? alertData.button : ''}
                onClose={() => setAlertData({ show: false })}
                content={alertData.text ? alertData.text : ''}
              />
              <ConfirmBottomSheet
                show={confirmData.show}
                title={confirmData.title ? confirmData.title : ''}
                onClose={() => setConfirmData({ show: false })}
                onConfirm={() => {
                  if (confirmData.onConfirm) confirmData.onConfirm();
                }}
                content={<TextModal>{confirmData.text}</TextModal>}
              />
            </SectionContainer>
          )}

          <CompanyProfileSuccess show={showSuccess} />
        </>
      )}
    </>
  );
};

export default PaymentCardSelection;
